'use client'

import { create } from 'zustand'

import type { ReactNode } from 'react'

/**
 * 레이어 스택 — 레퍼런스 openLayer/openModal/openSheet/closeTop 을 옮겼다.
 * 모달·시트가 겹쳐 뜨고(해금 → 충전), 맨 위 것만 Esc·바깥 클릭에 반응한다.
 * 쌓기·닫기 순서만 여기서 관리하고, 그리는 것(포커스·포털·애니메이션)은 layer-host 가 Base UI Dialog 로 한다.
 */
type LayerKind = 'modal' | 'sheet' | 'raw'

export type LayerOptions = {
  surface?: 'glass' | 'solid'
  /** 바깥 클릭으로 닫히지 않게 (약관·인증 같은 필수 단계) */
  lock?: boolean
  /** 배경을 옅게 (앱 설치 시트) */
  clear?: boolean
  /** 시트 높이를 88% 로 고정 (충전소) */
  tall?: boolean
  /** 시트를 아래가 아니라 왼쪽에서 꺼낸다 — 햄버거 메뉴(nav-drawer). 모바일 기둥(480) 왼쪽 끝에 붙는다 */
  side?: 'left'
  /** 모달/시트 본문에 덧붙일 클래스 */
  className?: string
  /** 기본 모달 틀(여백·배경) 없이 내용이 자기 틀을 그린다 — 보조 화면 기록 창 */
  bare?: boolean
  /** 같은 key 의 레이어가 이미 열려 있으면 새로 쌓지 않고 내용만 바꾼다 */
  key?: string
  onClose?: () => void
  label?: string
}

export type Layer = LayerOptions & {
  id: string
  kind: LayerKind
  content: ReactNode
  closing: boolean
}

type OverlayState = {
  layers: Layer[]
  externalLayerId: string | null
  suspendForExternal: (id: string) => boolean
  resumeFromExternal: (id: string) => void
  open: (kind: LayerKind, content: ReactNode, options?: LayerOptions) => string
  close: (id?: string) => void
  closeAll: () => void
}

let seq = 0

export const useOverlayStore = create<OverlayState>()((set, get) => ({
  layers: [],
  externalLayerId: null,
  suspendForExternal: (id) => {
    if (get().externalLayerId) return false
    set({ externalLayerId: id })
    return true
  },
  resumeFromExternal: (id) => {
    if (get().externalLayerId === id) set({ externalLayerId: null })
  },
  open: (kind, content, options = {}) => {
    const existing = options.key
      ? get().layers.find((l) => l.key === options.key && !l.closing)
      : undefined
    if (existing) {
      set({
        layers: get().layers.map((l) =>
          l.id === existing.id ? { ...l, ...options, content } : l
        ),
      })
      return existing.id
    }
    const id = `layer-${++seq}`
    set({
      layers: [
        ...get().layers,
        { ...options, id, kind, content, closing: false },
      ],
    })
    return id
  },
  close: (id) => {
    const live = get().layers.filter((l) => !l.closing)
    const target = id ? live.find((l) => l.id === id) : live[live.length - 1]
    if (!target) return
    set({
      layers: get().layers.map((l) =>
        l.id === target.id ? { ...l, closing: true } : l
      ),
    })
    target.onClose?.()
    // 닫힘 애니메이션(모달 200ms · 시트 240ms, layer-host)이 끝난 뒤 목록에서 뺀다
    setTimeout(
      () => set({ layers: get().layers.filter((l) => l.id !== target.id) }),
      280
    )
  },
  closeAll: () => {
    const live = get().layers.filter((l) => !l.closing)
    for (let i = live.length - 1; i >= 0; i--) get().close(live[i].id)
  },
}))

export const openModal = (content: ReactNode, options?: LayerOptions) =>
  useOverlayStore.getState().open('modal', content, options)
export const openSheet = (content: ReactNode, options?: LayerOptions) =>
  useOverlayStore.getState().open('sheet', content, options)
export const closeTop = () => useOverlayStore.getState().close()
export const closeLayer = (id: string) => useOverlayStore.getState().close(id)
export const closeAll = () => useOverlayStore.getState().closeAll()
export const hasLayers = () =>
  useOverlayStore.getState().layers.some((l) => !l.closing)
